import type { ApiErrorInputsResponse, ApiNewErrorResponse } from '@/shared/model/types/error';

export type NormalizedApiError = {
  format: 'new' | 'inputs' | 'unknown';
  httpStatus?: number;
  code?: string | number;
  message: string;
  details?: Record<string, unknown>;
  fields?: Record<string, string[]>;
};

const UNKNOWN_ERROR_MESSAGE = 'Произошла неизвестная ошибка';
const INVALID_FORMAT_MESSAGE = 'Неверный формат';

const localizeLegacyMessage = (message: string): string | undefined => {
  const normalized = message
    .trim()
    .replace(/[.!]+$/, '')
    .toLowerCase();

  if (normalized === 'invalid data') return 'Некорректные данные';
  if (
    normalized === 'required' ||
    normalized === 'field is required' ||
    normalized === 'this field is required' ||
    normalized === 'this is a required field' ||
    normalized === 'this field may not be blank' ||
    normalized === 'this field may not be null'
  ) {
    return 'Обязательное поле';
  }
  if (normalized === 'invalid format' || normalized === 'invalid value') return INVALID_FORMAT_MESSAGE;
  if (normalized === 'invalid credentials' || normalized === 'invalid username or password') {
    return 'Неверный логин или пароль';
  }
  if (normalized === 'passwords do not match' || normalized === 'password confirmation does not match') {
    return 'Пароли не совпадают';
  }

  const minLength = normalized.match(
    /^(?:this field )?must be at least (\d+) characters?$|^ensure this field has at least (\d+) characters?$/,
  );
  if (minLength) return `Минимальная длина ${minLength[1] || minLength[2]} символов`;

  const maxLength = normalized.match(
    /^(?:this field )?must be at most (\d+) characters?$|^ensure this field has no more than (\d+) characters?$/,
  );
  if (maxLength) return `Максимальная длина ${maxLength[1] || maxLength[2]} символов`;

  if (/^(?:this field )?must be a valid (email|url|date)$/.test(normalized)) return INVALID_FORMAT_MESSAGE;
  if (/^(?:enter a valid|invalid) (email|url|phone number|date)/.test(normalized)) return INVALID_FORMAT_MESSAGE;
  if (/^(?:this field )?must be a string$/.test(normalized)) return INVALID_FORMAT_MESSAGE;

  return undefined;
};

const localizeMessage = (message: unknown, fallback = UNKNOWN_ERROR_MESSAGE): string => {
  if (typeof message !== 'string' || message.trim() === '') return fallback;

  const trimmed = message.trim();
  const legacyMessage = localizeLegacyMessage(trimmed);
  if (legacyMessage) return legacyMessage;

  if (/[А-Яа-яЁё]/.test(trimmed)) return trimmed;

  return fallback;
};

const normalizeFields = (fields: unknown): Record<string, string[]> | undefined => {
  if (typeof fields !== 'object' || fields == null) return undefined;

  const normalizedFields: Record<string, string[]> = {};
  Object.entries(fields).forEach(([key, value]) => {
    normalizedFields[key] = Array.isArray(value)
      ? value.map((message) => localizeMessage(message, INVALID_FORMAT_MESSAGE))
      : [INVALID_FORMAT_MESSAGE];
  });

  return normalizedFields;
};

const getDetails = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value != null ? (value as Record<string, unknown>) : undefined;

export const normalizeApiError = (error: unknown): NormalizedApiError | null => {
  if (typeof error !== 'object' || error == null) {
    return null;
  }

  if ('status' in error && (error as any).status === 'FETCH_ERROR') {
    return {
      format: 'unknown',
      message: 'Нет соединения с сетью. Проверьте интернет.',
    };
  }

  if (!('data' in error)) {
    return null;
  }

  const data = (error as any).data;
  const status = typeof (error as any).status === 'number' ? (error as any).status : undefined;

  if (typeof data === 'object' && data != null && 'code' in data && 'message' in data) {
    const e = error as ApiNewErrorResponse;
    const payload = e.data;
    const details = getDetails(payload.details);
    const fields = details ? normalizeFields(details.fields) : undefined;

    return {
      format: 'new',
      httpStatus: status,
      code: payload.code,
      message: localizeMessage(payload.message),
      details,
      fields,
    };
  }

  if (
    typeof data === 'object' &&
    data != null &&
    'error' in data &&
    typeof data.error === 'string' &&
    (data.error === 'Не корректные данные' ||
      data.error === 'Некорректные данные' ||
      localizeLegacyMessage(data.error) === 'Некорректные данные') &&
    'details' in data
  ) {
    const e = error as ApiErrorInputsResponse;
    const fields = normalizeFields(e.data.details);

    return {
      format: 'inputs',
      httpStatus: status,
      message: localizeMessage(data.error, 'Некорректные данные'),
      fields,
    };
  }

  const dataRecord = getDetails(data);
  const details = getDetails(dataRecord?.details);
  const code = dataRecord?.code ?? details?.code;
  const message = dataRecord?.message ?? dataRecord?.error;

  return {
    format: 'unknown',
    httpStatus: status,
    code: typeof code === 'string' || typeof code === 'number' ? code : undefined,
    details,
    message: localizeMessage(message),
  };
};
